import { format, isSameYear } from 'date-fns';

import { proxyGet } from '../utils/proxy';
import zonedDate from '../utils/zoned-date';
import { sortBy } from 'lodash';

let _eventsPromise = null;
let _peoplePromise = null;
let _blogsPromise = null;
const _eventPerformancesPromises = {};

const now = zonedDate();
// The availability label can be overridden per instance in Basker with free text, so
// bookability is decided by the states that actually block a sale rather than by an
// allowlist of labels — a custom label like "Almost Sold Out" must not disable the
// Buy tickets button. The computed branch below only ever yields Good, Low, Limited
// or Sold Out; 'Cancelled' is reachable only via the override.
const BLOCKING_STATUSES = ['Sold Out', 'Cancelled'];

// Always pass an explicit limit — Basker's default changed to 500, which both
// bloats the first request and collapses the feed to a single page.
const EVENT_INSTANCES_PAGE_SIZE = 100;

// A single event's instances are fetched in one go, so this only needs to be
// comfortably above the most instances any one event can have.
const SINGLE_EVENT_PAGE_SIZE = 500;

export function getEvents() {
    if (_eventsPromise) {
        return _eventsPromise;
    }

    function fetchPage(page) {
        return proxyGet(`/event-instances.json?page=${page}&limit=${EVENT_INSTANCES_PAGE_SIZE}`);
    }

    function mapDocs(data) {
        return (data.docs || []).map(event => {
            event = processKeywords(event);
            event = processPerformance(event);
            return event;
        });
    }

    return _eventsPromise = fetchPage(1).then(data => {
        const allEvents = mapDocs(data);

        // A single page is a valid response, not a no-op.
        if (!data.totalPages || data.totalPages <= 1) {
            return allEvents;
        }

        const promises = [];
        for (let i = 2; i <= data.totalPages; i++) {
            promises.push(fetchPage(i));
        }

        return Promise.all(promises)
            .then(responses => {
                return responses.reduce((acc, response) => [...acc, ...mapDocs(response)], allEvents);
            });
    }).catch(error => {
        // Don't memoise the failure — otherwise one bad page poisons the feed for
        // the life of the page and no caller can retry without a reload.
        _eventsPromise = null;
        throw error;
    });
}

function transformPerformances(events) {
    let performances = [];

    for (const bookableItem of events) {
        const data = {
            ...bookableItem,
        }

        if(data.performancedate) {
            data._auxiliary = {
                group_by_day: format(data.performancedate, 'yyyy-MM-dd'),
                group_by_month: format(data.performancedate, 'yyyy-MM'),
                group_name_month: isSameYear(now, data.performancedate) ? format(data.performancedate, 'MMMM') : format(data.performancedate, 'MMMM yyyy'),
            }
        }

        performances.push(data);
    }

    // performances = performances.filter(item => {
    //     return +zonedDate(item.performancedate) > +now;
    // });

    return sortBy(performances, item => {
        return +item.performancedate;
    });
}

export function getPerformances() {
    return getEvents().then(response => {
        return transformPerformances(response);
    });
}

export function getEventPerformances(eventId) {
    if (_eventPerformancesPromises[eventId]) {
        return _eventPerformancesPromises[eventId];
    }

    // A single event's instances fit in one page, so no pagination here — but pass
    // the limit explicitly rather than inheriting whatever Basker defaults to.
    return _eventPerformancesPromises[eventId] = proxyGet(`/event-instances.json?eventId=${eventId}&limit=${SINGLE_EVENT_PAGE_SIZE}`)
        .then(data => {
            if (data.totalPages > 1) {
                console.warn(`Event ${eventId} has more instances than one page; some are not shown`);
            }

            const events = (data.docs || []).map(event => {
                event = processKeywords(event);
                event = processPerformance(event);
                return event;
            });

            return transformPerformances(events);
        });
}

export function getBlogs() {
    if (_blogsPromise) {
        return _blogsPromise
    }

    _blogsPromise = proxyGet(`https://d7iwuo0t5zwj9.cloudfront.net/proxy/basker/blogs`, null, null, true)
        .then(response => {
            return response.docs;
        });

    return _blogsPromise;
}

export function getPeople() {
    if (_peoplePromise) {
        return _peoplePromise
    }

    _peoplePromise = proxyGet(`https://d7iwuo0t5zwj9.cloudfront.net/proxy/basker/people`, null, null, true)
        .then(response => {
            return response.docs;
        });

    return _peoplePromise;
}

/**
 * Mimic Shopify's handleize function
 *
 * https://github.com/Shopify/liquid/blob/42b6763546f218dc8ad1184e10c5e2d9a262b006/performance/shopify/shop_filter.rb#L97-L106
 */
function handleize(string) {
    if (!string) {
        return '';
    }

    return string
        .toString()
        .toLowerCase()
        .replace(/['"()[]]/g, '')
        .replace(/\W+/g, '-')
        .replace(/-+$/, '')
        .replace(/^-+/, '');
}

function processKeywords(bookableItem) {
    const categories = [];
    const eventTypes = [];
    const venue = [];
    const timeOfDay = [];
    const genre = [];
    const season = [];
    const date = zonedDate(bookableItem.startDateLocalAsUTC);

    const eventTypesArray = bookableItem.attributes?.custom?.event_type ?? [];
    eventTypesArray.map(type => {
        categories.push(type);
    });

    if (!bookableItem.event?.venue?.attributes?.custom?.hide_on_calendar_filter) {
        venue.push({ title: bookableItem.event?.venue?.title });
    }

    timeOfDay.push({
        title: date.getHours() >= 18 ? 'Evening' : 'Daytime',
    });

    bookableItem.event.attributes?.custom?.genres?.forEach(genreItem => {
        genre.push({ title: genreItem });
    });

    categories.length ? categories.map(category => {
        eventTypes.push({ title: category });
    }) : [];

    if (bookableItem.event?.attributes?.custom?.season) {
        season.push({
            title: bookableItem.event.attributes.custom.season,
        });
    }

    bookableItem.filters = {
        categories: categories,
        venue_filters: venue,
        event_type_filters: eventTypes,
        time_of_day: timeOfDay,
        genre: genre,
        season: season,
    };

    return bookableItem;
}

function processPerformance(bookableItem) {
    bookableItem.additional_info = {
        approximate_running_time: bookableItem.event?.attributes?.custom?.approximate_running_time || null,
        recommended_age: bookableItem.event?.attributes?.custom?.recommended_age || null,
    };
    // Below can be used if the "Group" field type is working in Basker
    // bookableItem.additional_info = bookableItem.event?.attributes?.custom?.additional_event_info;

    bookableItem.hideStartTime = bookableItem.hideStartTime || bookableItem.event.hideStartTime;
    bookableItem.venue = bookableItem.event.venue;
    bookableItem.title = bookableItem.event.title;
    bookableItem.prefix = bookableItem.event.prefix;
    bookableItem.performancedate = zonedDate(bookableItem.startDateLocalAsUTC);
    bookableItem.description = bookableItem.event.description;
    bookableItem.image = bookableItem.event?.image || null;
    bookableItem.availability = parseInt(bookableItem.tessitura.availability);

    const availabilityOverride = bookableItem.attributes?.custom?.availability_override;

    // Editors treat the override as the customer-facing wording for this instance, so
    // it replaces the CTA text as well as the availability label. Kept as its own
    // field because availability_level is also populated by the computed branch, and
    // "Good"/"Low" are not button labels.
    bookableItem.availability_override = availabilityOverride || null;

    if (availabilityOverride) {
        bookableItem.availability_level = availabilityOverride;
    } else {
        if (bookableItem.availability < 1) {
            bookableItem.availability_level = 'Sold Out';
        } else {
            // Fall back between instance, event & venue
            const capacity = parseInt(bookableItem.attributes?.custom?.capacity) ||
                parseInt(bookableItem.event.attributes?.custom?.capacity) ||
                parseInt(bookableItem.event.venue?.attributes?.custom?.capacity) || 0;
            if (capacity) {
                const availabilityPercentage = (bookableItem.availability / capacity);
                if (availabilityPercentage >= .5) {
                    bookableItem.availability_level = 'Good';
                } else if (availabilityPercentage >= .2) {
                    bookableItem.availability_level = 'Low';
                } else {
                    bookableItem.availability_level = 'Limited';
                }
            } else {
                // Fallback if no capacity is set on the venue
                bookableItem.availability_level = 'Good';
            }
        }
    }

    bookableItem.is_bookable = !BLOCKING_STATUSES.includes(bookableItem.availability_level);
    bookableItem.availability_level_handle = handleize(bookableItem.availability_level);

    if (bookableItem.tessitura.performanceId) {
        bookableItem.tickets_url = `${AppConfig.boDomain}booking/production/${bookableItem.tessitura.performanceId}`;
    } else {
        bookableItem.tickets_url = bookableItem.booking.bookingLinkURL ?? bookableItem.event.booking.bookingLinkURL;
    }

    bookableItem.ctaPrimary = {
        title: bookableItem?.booking?.bookingLinkLabel ?? bookableItem.event?.booking?.bookingLinkLabel ?? 'Buy Tickets',
        link: bookableItem.tickets_url,
    };

    bookableItem.ctaSecondary = bookableItem.event.url ? {
        title: 'More Info',
        link: bookableItem.event.url,
    } : null;

    const date = bookableItem.performancedate;
    const hour = format(date, 'h');
    const minutes = format(date, 'mm');
    const period = format(date, 'a').toLowerCase();

    bookableItem.formattedTime = minutes === '00' ? `${hour}${period}` : `${hour}:${minutes}${period}`;

    return bookableItem;
}
